type SoundName =
  | 'attack' | 'heavy_attack' | 'skill'
  | 'dodge' | 'shield' | 'dash'
  | 'hit_taken' | 'boss_hit' | 'boss_death'
  | 'challenge_success' | 'challenge_fail'
  | 'battle_start' | 'victory' | 'defeat'
  | 'ui_click' | 'ui_hover' | 'combo';

class SoundManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;

  private getContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.3;
      this.masterGain.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  private playTone(freq: number, duration: number, type: OscillatorType = 'square', gain = 0.3) {
    const ctx = this.getContext();
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    env.gain.setValueAtTime(gain, ctx.currentTime);
    env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(env);
    if (this.masterGain) env.connect(this.masterGain);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  }

  private playNoise(duration: number, gain = 0.15) {
    const ctx = this.getContext();
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const env = ctx.createGain();
    env.gain.setValueAtTime(gain, ctx.currentTime);
    env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 2000;
    source.connect(filter);
    filter.connect(env);
    if (this.masterGain) env.connect(this.masterGain);
    source.start(ctx.currentTime);
  }

  play(name: SoundName) {
    switch (name) {
      case 'attack':
        this.playTone(880, 0.08, 'square', 0.2);
        break;
      case 'heavy_attack':
        this.playTone(440, 0.15, 'sawtooth', 0.3);
        this.playTone(220, 0.2, 'square', 0.2);
        break;
      case 'skill':
        this.playTone(660, 0.1, 'sine', 0.25);
        setTimeout(() => this.playTone(990, 0.15, 'sine', 0.2), 50);
        break;
      case 'dodge':
        this.playNoise(0.12, 0.1);
        this.playTone(600, 0.1, 'sine', 0.15);
        break;
      case 'dash':
        this.playNoise(0.18, 0.12);
        this.playTone(800, 0.12, 'sine', 0.15);
        break;
      case 'shield':
        this.playTone(300, 0.3, 'sine', 0.15);
        break;
      case 'hit_taken':
        this.playTone(180, 0.15, 'sawtooth', 0.25);
        this.playNoise(0.1, 0.12);
        break;
      case 'boss_hit':
        this.playTone(220, 0.1, 'square', 0.3);
        this.playTone(110, 0.15, 'sawtooth', 0.2);
        break;
      case 'boss_death':
        this.playTone(440, 0.3, 'sawtooth', 0.3);
        setTimeout(() => this.playTone(220, 0.4, 'sawtooth', 0.25), 100);
        setTimeout(() => this.playTone(110, 0.6, 'sawtooth', 0.2), 250);
        this.playNoise(0.5, 0.2);
        break;
      case 'challenge_success':
        this.playTone(880, 0.1, 'sine', 0.25);
        setTimeout(() => this.playTone(1100, 0.15, 'sine', 0.25), 80);
        break;
      case 'challenge_fail':
        this.playTone(300, 0.2, 'square', 0.2);
        this.playTone(200, 0.3, 'square', 0.15);
        break;
      case 'battle_start':
        this.playTone(440, 0.15, 'sine', 0.2);
        setTimeout(() => this.playTone(554, 0.15, 'sine', 0.2), 120);
        setTimeout(() => this.playTone(659, 0.2, 'sine', 0.25), 240);
        break;
      case 'victory':
        this.playTone(523, 0.2, 'sine', 0.3);
        setTimeout(() => this.playTone(659, 0.2, 'sine', 0.3), 150);
        setTimeout(() => this.playTone(784, 0.2, 'sine', 0.3), 300);
        setTimeout(() => this.playTone(1047, 0.4, 'sine', 0.35), 450);
        break;
      case 'defeat':
        this.playTone(400, 0.3, 'sawtooth', 0.2);
        setTimeout(() => this.playTone(300, 0.4, 'sawtooth', 0.15), 200);
        setTimeout(() => this.playTone(200, 0.5, 'sawtooth', 0.1), 400);
        break;
      case 'ui_click':
        this.playTone(600, 0.05, 'square', 0.15);
        break;
      case 'ui_hover':
        this.playTone(800, 0.04, 'sine', 0.08);
        break;
      case 'combo':
        this.playTone(660, 0.08, 'sine', 0.2);
        break;
    }
  }
}

export const soundManager = new SoundManager();
